import { createEffect, createMemo, For, lazy, onCleanup, Show, Suspense } from "solid-js"
import { createStore } from "solid-js/store"
import { createResizeObserver } from "@solid-primitives/resize-observer"
import { createMemoryHistory, MemoryRouter, Route, useBeforeLeave } from "@solidjs/router"
import { useLanguage } from "@/context/language"
import { isChatDockPath, localWorkspacePath } from "@/cowork/chat-dock"
import { TargetSessionRoute } from "@/pages/target-session-route"
import { CoworkChatContext } from "./cowork-chat-context"
import "./cowork-chat-dock.css"

const NewSession = lazy(() => import("@/pages/new-session"))

export function CoworkChatDock(props: {
  path: string; workspacePath: string; open: boolean; expanded: boolean; onClose: () => void; onNew: () => void;
  onExpand: () => void; onPath: (path: string) => void; onNavigate: (path: string) => void;
  sessions?: readonly { path: string; title: string; working?: boolean }[];
  onSessionSelect?: (path: string) => void;
  onSessionClose?: (path: string) => void;
  onSessionReconnect?: (path: string) => void;
  reconnectingPath?: string;
  reconnectError?: boolean;
}) {
  const language = useLanguage()
  const [scroll, setScroll] = createStore({ left: false, right: false })
  let viewport: HTMLDivElement | undefined
  let strip: HTMLDivElement | undefined
  let frame: number | undefined
  const buttons = new Map<string, HTMLButtonElement>()
  const updateScroll = () => {
    if (!viewport) return
    const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth)
    const left = getComputedStyle(viewport).direction === "rtl" ? max + viewport.scrollLeft : viewport.scrollLeft
    setScroll({ left: left > 1, right: left < max - 1 })
  }
  const revealActive = () => {
    if (frame !== undefined) cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      frame = undefined
      const active = buttons.get(props.path)
      if (viewport && active && props.open) {
        const bounds = viewport.getBoundingClientRect(), tab = active.getBoundingClientRect()
        const delta = tab.left < bounds.left ? tab.left - bounds.left : Math.max(0, tab.right - bounds.right)
        if (delta) viewport.scrollBy({ left: delta, behavior: "instant" })
      }
      updateScroll()
    })
  }
  createResizeObserver(() => [viewport, strip].filter((element): element is HTMLDivElement => !!element), revealActive)
  const layoutKey = createMemo(() => JSON.stringify([props.path, props.open, props.sessions?.map((session) => [session.path, session.title, session.working])]))
  createEffect(() => { layoutKey(); revealActive() })
  onCleanup(() => { if (frame !== undefined) cancelAnimationFrame(frame) })
  const scrollTabs = (direction: number) => viewport?.scrollBy({ left: direction * viewport.clientWidth * .75,
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" })
  const history = createMemoryHistory()
  let lastChat = isChatDockPath(props.path) ? props.path : "/new-session"
  history.set({ value: lastChat, replace: true })
  const stop = history.listen((path) => {
    if (isChatDockPath(path)) { lastChat = path; props.onPath(path); return }
    // Output links open in the accounting workspace without replacing the conversation.
    if (localWorkspacePath(path)) props.onNavigate(path)
    queueMicrotask(() => history.set({ value: lastChat, replace: true }))
  })
  onCleanup(stop)
  createEffect(() => {
    if (isChatDockPath(props.path) && history.get() !== props.path) history.set({ value: props.path, replace: true })
  })
  let dock: HTMLElement | undefined
  createEffect(() => {
    if (props.open) requestAnimationFrame(() => dock?.querySelector<HTMLButtonElement>("[data-dock-close]")?.focus())
  })
  return <aside ref={dock} class="cowork-chat-dock" classList={{ expanded: props.expanded }} hidden={!props.open}
    aria-label={language.t("cowork.chatDock.title")} onKeyDown={(event) => {
      if (event.key === "Escape" && !event.defaultPrevented) { event.stopPropagation(); props.onClose() }
      if (event.key !== "Tab" || !window.matchMedia("(max-width: 900px)").matches) return
      const items = [...(dock?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [contenteditable="true"], [tabindex="0"]') ?? [])].filter((item) => item.getClientRects().length)
      const first = items[0], last = items.at(-1)
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }}>
    <header class="cowork-chat-dock-header"><div><h2>{language.t("cowork.chatDock.title")}</h2><p>{language.t("cowork.chatDock.documents")}</p></div>
      <div><button type="button" onClick={props.onNew} aria-label={language.t("cowork.enterprise.newChat")} title={language.t("cowork.enterprise.newChat")}>＋</button>
        <button type="button" class="dock-expand" onClick={props.onExpand} aria-label={language.t(props.expanded ? "cowork.chatDock.restore" : "cowork.chatDock.expand")} title={language.t(props.expanded ? "cowork.chatDock.restore" : "cowork.chatDock.expand")}>{props.expanded ? "↙" : "↗"}</button>
        <button type="button" data-dock-close onClick={props.onClose} aria-label={language.t("cowork.chatDock.close")} title={language.t("cowork.chatDock.close")}>×</button></div>
    </header>
    <CoworkChatContext path={props.path} workspacePath={props.workspacePath} active={props.open} />
    <div class="cowork-chat-dock-sessions">
      <Show when={scroll.left || scroll.right}><button class="dock-scroll" disabled={!scroll.left} aria-label={language.t("cowork.enterprise.chats.scrollLeft")} onClick={() => scrollTabs(-1)}>‹</button></Show>
      <div ref={viewport} class="dock-tabs-viewport" onScroll={updateScroll}><div ref={strip} role="tablist" aria-label={language.t("cowork.sessions.title")} class="dock-tabs">
        <For each={props.sessions?.map((session) => session.path) ?? []}>{(path, index) => {
          const current = () => props.sessions?.find((session) => session.path === path)
          const session = { path, get title() { return current()?.title ?? "" }, get working() { return current()?.working } }
          return <div class="dock-tab" classList={{ active: props.path === session.path }} role="presentation">
          <button type="button" role="tab" data-session-path={session.path} aria-selected={props.path === session.path} aria-busy={session.working ?? false}
            ref={(element) => { buttons.set(session.path, element); onCleanup(() => buttons.delete(session.path)) }}
            tabIndex={props.path === session.path || (!props.sessions?.some((item) => item.path === props.path) && index() === 0) ? 0 : -1}
            title={session.title} onClick={() => { if (props.path !== session.path) props.onSessionSelect?.(session.path) }}
            onKeyDown={(event) => {
              if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return
              event.preventDefault()
              const sessions = props.sessions ?? []
              const next = event.key === "Home" ? 0 : event.key === "End" ? sessions.length - 1 :
                (index() + (event.key === "ArrowRight" ? 1 : -1) + sessions.length) % sessions.length
              const target = sessions[next]
              if (target) { buttons.get(target.path)?.focus(); props.onSessionSelect?.(target.path) }
            }}>
            <Show when={session.working}><span class="dock-tab-spinner" role="img" aria-label={language.t("cowork.chatDock.processing")} /></Show>
            <span class="dock-tab-title">{session.title}</span>
          </button>
          <Show when={props.onSessionClose}><button type="button" class="dock-tab-close"
            aria-label={language.t("cowork.enterprise.chats.closeTab")} title={language.t("cowork.enterprise.chats.closeTab")}
            onClick={() => props.onSessionClose?.(session.path)}>×</button></Show>
        </div>}}</For>
        <Show when={!props.sessions?.length}><span class="dock-tabs-empty">{language.t("cowork.enterprise.newChat")}</span></Show>
      </div></div>
      <Show when={scroll.left || scroll.right}><button class="dock-scroll" disabled={!scroll.right} aria-label={language.t("cowork.enterprise.chats.scrollRight")} onClick={() => scrollTabs(1)}>›</button></Show>
    </div>
    <div class="cowork-chat-dock-content">
      <Suspense fallback={<p class="cowork-chat-dock-loading" role="status">{language.t("common.loading")}</p>}>
        <MemoryRouter history={history} root={(route) => {
          useBeforeLeave((event) => {
            if (typeof event.to === "string" && localWorkspacePath(event.to) && !isChatDockPath(event.to)) {
              event.preventDefault()
              props.onNavigate(event.to)
            }
          })
          return route.children
        }}>
          <Route path="/new-session" component={NewSession} />
          <Route path="/server/:serverKey/session/:id" component={() => <TargetSessionRoute
            onReconnect={props.onSessionReconnect ? () => props.onSessionReconnect?.(props.path) : undefined}
            reconnecting={props.reconnectingPath === props.path} reconnectError={props.reconnectError} />} />
          <Route path="*" component={() => null} />
        </MemoryRouter>
      </Suspense>
    </div>
  </aside>
}
